import { getSentryRelease } from "~/common/utils";
import { errorReporter } from "~/error-handling/errorReporter";
import { toError } from "~/error-handling/errorUtils";

interface VersionInfo {
  version: string;
  commitHash: string;
  shortHash: string;
  buildTime: string;
}

class VersionCheckService {
  private currentVersion: string | null = null;
  private checkInterval: NodeJS.Timeout | null = null;

  // Get the version that was bundled with the app
  getCurrentVersion() {
    if (!this.currentVersion) {
      // This should be set during build
      const currentRelease = getSentryRelease();
      const commitHash = currentRelease.split("@")[1];
      this.currentVersion = commitHash || "unknown";
    }
    return this.currentVersion;
  }

  // Fetch the latest version from the server
  async getLatestVersion() {
    try {
      const response = await fetch("/version.json", {
        cache: "no-cache",
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      return (await response.json()) as VersionInfo;
    } catch (error) {
      const errObj = toError(error, "Failed to fetch version info");
      console.warn(errObj);
      errorReporter.reportApplicationError(errObj, {
        level: "warning",
        tags: {
          operation: "versionCheck",
        },
      });
      return null;
    }
  }

  // Check if there's a new version available
  async checkForUpdates() {
    const latest = await this.getLatestVersion();
    if (!latest) return false;

    const current = this.getCurrentVersion();
    return latest.commitHash !== current;
  }

  // Start periodic checking
  startPeriodicCheck(
    intervalMs = 60 * 60 * 1000, // 1 hour
    onUpdateAvailable: () => void,
  ) {
    this.stopPeriodicCheck(); // Clear any existing interval

    this.checkInterval = setInterval(async () => {
      const hasUpdate = await this.checkForUpdates();
      if (hasUpdate) {
        onUpdateAvailable();
      }
    }, intervalMs);
  }

  // Stop periodic checking
  stopPeriodicCheck() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }
}

export const versionCheckService = new VersionCheckService();
